/**
 * The validation screen: the document on the left, what was read on the
 * right, and one click between a number and the place it came from.
 *
 * Three things it is built around.
 *
 * **Selecting a field locates it on the page.** That is the entire job
 * of this screen, so it is the default interaction rather than a feature
 * behind a button: move through the fields with the arrow keys and the
 * document follows.
 *
 * **The arithmetic is shown, not asserted.** Subtotal, tax and total are
 * laid out as a sum with its result, because that is the check a person
 * does first and an interface that reports "valid" is asking to be
 * trusted rather than showing its working.
 *
 * **Findings point at fields.** Clicking one selects the field it is
 * about and locates it on the page. A rule code in a list somewhere else
 * makes the reviewer do the join by eye.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import type { Shell } from "./App";
import { api } from "./api";
import { bytes, money, ratePercent, shortDate, timestamp } from "./format";
import { PdfPane } from "./PdfPane";
import type { DocumentDetail, Extraction, Finding, Invoice } from "./types";
import { Chip, Icon, StatusChip } from "./ui";

interface FieldSpec {
  path: string;
  label: string;
  value: string | null;
  /** What to look for on the page, when it differs from what is shown. */
  locate?: string | null;
}

function headerFields(invoice: Invoice): FieldSpec[] {
  return [
    { path: "invoice_number", label: "Invoice number", value: invoice.invoice_number },
    { path: "issue_date", label: "Issue date", value: shortDate(invoice.issue_date) || null },
    { path: "due_date", label: "Due date", value: shortDate(invoice.due_date) || null },
    { path: "currency", label: "Currency", value: invoice.currency },
    { path: "vendor.name", label: "Vendor", value: invoice.vendor_name },
    { path: "vendor.tax_id", label: "Vendor tax id", value: invoice.vendor_tax_id },
    { path: "vendor.address", label: "Vendor address", value: invoice.vendor_address },
    { path: "buyer.name", label: "Buyer", value: invoice.buyer_name },
    { path: "buyer.address", label: "Buyer address", value: invoice.buyer_address },
    { path: "buyer.tax_id", label: "Buyer tax id", value: invoice.buyer_tax_id },
    {
      path: "subtotal",
      label: "Subtotal",
      value: money(invoice.subtotal, invoice.currency) || null,
      locate: invoice.subtotal,
    },
    {
      path: "tax_amount",
      label: "Tax",
      value: money(invoice.tax_amount, invoice.currency) || null,
      locate: invoice.tax_amount,
    },
    {
      path: "total",
      label: "Total",
      value: money(invoice.total, invoice.currency) || null,
      locate: invoice.total,
    },
  ];
}

export function ValidateView() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const shell = useOutletContext<Shell>();
  const [document, setDocument] = useState<DocumentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [attemptIndex, setAttemptIndex] = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const detail = await api.document(id);
      setDocument(detail);
      setAttemptIndex(null);
      setError(null);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const attempts = document?.extractions ?? [];
  const extraction: Extraction | undefined =
    attempts[attemptIndex ?? attempts.length - 1];
  const invoice = extraction?.invoice ?? null;

  const fields = useMemo(() => (invoice ? headerFields(invoice) : []), [invoice]);

  const flagged = useMemo(
    () =>
      new Set(
        (extraction?.findings ?? [])
          .map((finding) => finding.field_path ?? "")
          .filter(Boolean),
      ),
    [extraction],
  );

  // Arrow keys move through the fields; the document follows the
  // selection. A reviewer should be able to check a whole invoice
  // without touching the mouse.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (!fields.length) return;

      const index = fields.findIndex((field) => field.path === selected);
      if (event.key === "ArrowDown" || event.key === "j") {
        event.preventDefault();
        setSelected(fields[Math.min(fields.length - 1, index + 1)]?.path ?? null);
      } else if (event.key === "ArrowUp" || event.key === "k") {
        event.preventDefault();
        setSelected(fields[Math.max(0, index <= 0 ? 0 : index - 1)]?.path ?? null);
      } else if (event.key === "Escape") {
        setSelected(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fields, selected]);

  async function run(name: string, action: () => Promise<DocumentDetail>) {
    setBusy(name);
    try {
      const updated = await action();
      setDocument(updated);
      setAttemptIndex(null);
      setError(null);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setBusy(null);
    }
  }

  if (error && !document) return <div className="page"><div className="banner">{error}</div></div>;
  if (!document) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 260 }} />
      </div>
    );
  }

  const selectedField = fields.find((field) => field.path === selected) ?? null;
  const target = selectedField ? selectedField.locate ?? selectedField.value : null;

  return (
    <>
      <div className="subhead">
        <button className="btn ghost sm" onClick={() => navigate("/")}>
          {Icon.left({ size: 15 })} Queue
        </button>
        <div>
          <div className="title">{invoice?.vendor_name ?? document.filename}</div>
          <div className="meta">
            {document.filename} · {bytes(document.byte_size)}
            {document.page_count ? ` · ${document.page_count} pages` : ""} ·{" "}
            {document.source}
          </div>
        </div>

        {extraction && <StatusChip status={extraction.status} ok={extraction.ok} />}

        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn"
            onClick={() => void run("again", () => api.readAgain(id))}
            disabled={busy !== null}
          >
            {Icon.refresh({ size: 15 })} {busy === "again" ? "Reading" : "Read again"}
          </button>
          <a className="btn" href={api.exportUrl(id)}>
            {Icon.download({ size: 15 })} Export JSON
          </a>
          {/* The delete button is the whole privacy story of a demo with
              no accounts, so it is here beside the other actions rather
              than hidden in a menu. Only for uploads: the sample
              invoices are the demo, and one visitor must not be able to
              empty it for everybody. The confirm is deliberate - this
              removes the file from the server and cannot be undone. */}
          {document.source !== "synthetic" && (
            <button
              className="btn danger"
              onClick={() => {
                if (!window.confirm(
                  "Delete this document? The file and everything read from it " +
                  "are removed from the server. This cannot be undone."
                )) return;
                void (async () => {
                  try {
                    await api.remove(id);
                    // The bar counts documents too. Deleting one without
                    // telling it leaves the same disagreement uploading did.
                    shell?.documentsChanged();
                    navigate("/");
                  } catch (error) {
                    window.alert(
                      `Could not delete it: ${(error as Error).message}`
                    );
                  }
                })();
              }}
              disabled={busy !== null}
            >
              {Icon.trash({ size: 15 })} Delete
            </button>
          )}
          <button
            className="btn primary"
            onClick={() => void run("approve", () => api.approve(id))}
            disabled={busy !== null || !extraction?.ok || extraction?.status === "approved"}
          >
            {Icon.check({ size: 15 })}
            {extraction?.status === "approved" ? "Approved" : "Approve"}
          </button>
        </div>
      </div>

      <div className="page">
        {error && <div className="banner">{error}</div>}

        {!extraction ? (
          <div className="empty">This document has not been read yet.</div>
        ) : (
          <div className="validate">
            <PdfPane
              url={api.fileUrl(id)}
              target={target}
              available={document?.original_available !== false}
            />

            <div style={{ display: "grid", gap: 16 }}>
              {!extraction.ok && (
                <div className="banner">
                  {Icon.alert({ size: 18 })}
                  <div>
                    <strong>Nothing could be read from this document.</strong>
                    <p style={{ margin: "4px 0 0" }}>{extraction.error}</p>
                    <p style={{ margin: "8px 0 0" }} className="note">
                      It is kept and sits at the top of the queue. A document that
                      cannot be read is the most important one in the pile, not
                      the one to drop.
                    </p>
                  </div>
                </div>
              )}

              {invoice && (
                <>
                  <section className="card">
                    <div className="card-head">
                      <div>
                        <h2>What was read</h2>
                        <p>
                          Select a field to find it on the page. <span className="kbd">↑</span>{" "}
                          <span className="kbd">↓</span> to move, <span className="kbd">Esc</span>{" "}
                          to clear.
                        </p>
                      </div>
                      {flagged.size > 0 && (
                        <Chip tone="danger" icon={Icon.alert({ size: 13 })}>
                          {flagged.size} flagged
                        </Chip>
                      )}
                    </div>
                    <div className="fieldlist" role="listbox" aria-label="Extracted fields">
                      {fields.map((field) => (
                        <button
                          key={field.path}
                          role="option"
                          aria-selected={selected === field.path}
                          className={`fieldrow${flagged.has(field.path) ? " flagged" : ""}`}
                          onClick={() =>
                            setSelected(selected === field.path ? null : field.path)
                          }
                        >
                          <span className="label">
                            {field.label}
                            {flagged.has(field.path) && Icon.alert({ size: 12 })}
                          </span>
                          <span className={`value${field.value ? "" : " blank"}`}>
                            {field.value ?? "not found"}
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="card">
                    <div className="card-head">
                      <h2>Does it add up?</h2>
                      <span className="note">checked in decimal, not floating point</span>
                    </div>
                    <div className="reconcile">
                      <div className="reconcile-row">
                        <span>Subtotal</span>
                        <span className="num">
                          {money(invoice.subtotal, invoice.currency) || "—"}
                        </span>
                      </div>
                      <div className="reconcile-row">
                        <span>
                          Tax{invoice.tax_rate ? ` at ${ratePercent(invoice.tax_rate)}` : ""}
                        </span>
                        <span className="num">
                          {money(invoice.tax_amount, invoice.currency) || "—"}
                        </span>
                      </div>
                      <div className="reconcile-row total">
                        <span>Total</span>
                        <span className="num">
                          {money(invoice.total, invoice.currency) || "—"}
                        </span>
                      </div>

                      <div className={`verdict ${extraction.self_consistent ? "ok" : "bad"}`}>
                        {extraction.self_consistent
                          ? Icon.check({ size: 17 })
                          : Icon.alert({ size: 17 })}
                        <div>
                          <strong>
                            {extraction.self_consistent
                              ? "The document agrees with itself"
                              : "The document contradicts itself"}
                          </strong>
                          <p>
                            Rows, subtotal, tax and total were checked against each
                            other with a tolerance that scales with how many rounded
                            values were added. No ground truth is involved, so this
                            works on any document from anywhere.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <Findings
                    findings={extraction.findings}
                    onSelect={(path) => setSelected(path)}
                  />

                  <section className="card">
                    <div className="card-head">
                      <h2>Line items</h2>
                      <span className="note">{invoice.line_items.length} rows</span>
                    </div>
                    <div className="table-wrap">
                      <table className="data">
                        <thead>
                          <tr>
                            <th style={{ width: 38 }}>#</th>
                            <th>Description</th>
                            <th className="right">Qty</th>
                            <th className="right">Unit price</th>
                            <th className="right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.line_items.map((item) => (
                            <tr
                              key={item.position}
                              onClick={() => setSelected(null)}
                              style={{ cursor: "default" }}
                            >
                              <td className="num">{item.position + 1}</td>
                              <td className="strong">{item.description ?? "—"}</td>
                              <td className="right num">{item.quantity ?? ""}</td>
                              <td className="right num">
                                {money(item.unit_price, invoice.currency)}
                              </td>
                              <td className="right num">
                                {money(item.amount, invoice.currency)}
                              </td>
                            </tr>
                          ))}
                          {invoice.line_items.length === 0 && (
                            <tr>
                              <td colSpan={5} style={{ color: "var(--muted)" }}>
                                No rows were read from this document.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </>
              )}

              <Provenance
                document={document}
                extraction={extraction}
                attempts={attempts}
                onPick={setAttemptIndex}
                current={attemptIndex ?? attempts.length - 1}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Findings({
  findings,
  onSelect,
}: {
  findings: Finding[];
  onSelect: (path: string) => void;
}) {
  return (
    <section className="card">
      <div className="card-head">
        <h2>Findings</h2>
        <span className="note">{findings.length}</span>
      </div>
      {findings.length === 0 ? (
        <div style={{ padding: "14px 16px", color: "var(--muted)" }}>
          Nothing flagged. Every rule this document could break, it did not.
        </div>
      ) : (
        findings.map((finding, index) => (
          <button
            key={`${finding.code}-${index}`}
            className="finding"
            onClick={() => finding.field_path && onSelect(finding.field_path)}
          >
            <span
              className={`dot ${finding.severity === "error" ? "danger" : "warn"}`}
              style={{ marginTop: 6 }}
            />
            <div style={{ minWidth: 0 }}>
              <div className="code">{finding.code.replace(/_/g, " ")}</div>
              <p>{finding.message}</p>
              {finding.field_path && (
                <div className="where">
                  {finding.field_path} · click to locate on the page
                </div>
              )}
            </div>
          </button>
        ))
      )}
    </section>
  );
}

function Provenance({
  document,
  extraction,
  attempts,
  current,
  onPick,
}: {
  document: DocumentDetail;
  extraction: Extraction | undefined;
  attempts: Extraction[];
  current: number;
  onPick: (index: number) => void;
}) {
  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Provenance</h2>
          <p>
            Every reading is kept. Two readings that disagree are the finding, so
            nothing here overwrites anything.
          </p>
        </div>
        {extraction && extraction.provider !== "anthropic" && (
          <Chip tone="warn">Simulated</Chip>
        )}
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Attempt</th>
              <th>Provider</th>
              <th>Read at</th>
              <th className="right">Tokens</th>
              <th className="right">Suspicion</th>
            </tr>
          </thead>
          <tbody>
            {attempts.map((item, index) => (
              <tr
                key={item.id}
                onClick={() => onPick(index)}
                style={{
                  background: index === current ? "var(--accent-tint)" : undefined,
                }}
              >
                <td className="num">#{item.attempt}</td>
                <td>
                  {item.provider} / {item.model}
                </td>
                <td className="num">{timestamp(item.created_at)}</td>
                <td className="right num">
                  {item.input_tokens} / {item.output_tokens}
                </td>
                <td className="right num">{item.ok ? item.suspicion : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ padding: "12px 16px" }} className="note">
        SHA-256 <span className="mono">{document.sha256.slice(0, 32)}…</span>
      </div>
    </section>
  );
}
